/**
 * List Settings Modal
 * Full GetResponse list configuration with all fields
 */

import * as React from 'react';
import { useState, useEffect } from 'react';
import { X, Save, Settings, Upload, CheckCircle } from 'lucide-react';

interface ListSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  list: {
    campaignId: string;
    name: string;
    description?: string;
  } | null;
  onSave: (listId: string, settings: any) => Promise<void>;
}

const LIST_CATEGORIES = [
  'Agencies',
  'Arts and entertainment',
  'Automotive',
  'Communications',
  'Education',
  'Financial services',
  'Health and beauty',
  'Health care',
  'Internet marketing',
  'Legal services',
  'Non-profits',
  'Publishing',
  'Real estate',
  'Retail',
  'Technology',
  'Travel',
  'Other',
];

const LANGUAGES = [
  { code: 'EN', name: 'English' },
  { code: 'ES', name: 'Spanish' },
  { code: 'FR', name: 'French' },
  { code: 'DE', name: 'German' },
  { code: 'IT', name: 'Italian' },
  { code: 'PT', name: 'Portuguese' },
];

const ListSettingsModal: React.FC<ListSettingsModalProps> = ({
  isOpen,
  onClose,
  list,
  onSave,
}) => {
  const [saving, setSaving] = useState(false);
  
  // Simple settings - just name and description
  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');

  useEffect(() => {
    if (list) {
      setListName(list.name || '');
      setListDescription(list.description || '');
    }
  }, [list]);

  const handleSave = async () => {
    if (!list) return;

    try {
      setSaving(true);
      await onSave(list.campaignId, {
        name: listName,
        description: listDescription,
      });
      onClose();
    } catch (error) {
      console.error('Error saving list settings:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !list) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-indigo-600" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit List</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Simple Form - No Tabs */}
        <div className="space-y-5">
          {/* List Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              List Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter list name"
              autoFocus
            />
          </div>

          {/* List Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={listDescription}
              onChange={(e) => setListDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="Optional description for this list"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!listName || saving}
            className="flex-1 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {saving ? (
              <>Saving...</>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListSettingsModal;

